import { Box, Modal, sanitizeToNumeric } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostCreateWorkflow from '@/hooks/useApi/usePostCreateWorkflow';
import useDeleteWorkflow from '@/hooks/useApi/useDeleteWorkflow';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import CreateWorkflowForm from './CreateWorkflowForm';
import type { UpdateWorkflowContainer } from './types';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { Trans, useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { Title, Button, UtilButton } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import WorkflowCreatedDialog from './WorkflowCreatedDialog';
import SubAccountsWorkflowDialog from './SubAccountsWorkflowDialog';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import styles from './WorkflowContainer.module.scss';
import { propagateToSubAccounts, type RailStats } from './propagateToSubAccounts';

type CreateWorkflowContainerProps = {
  bearer?: string;
  accountId?: string;
  onBack?: () => void;
  onCreated?: () => void;
  onNavigateToRoles?: () => void;
};

type PendingDialog =
  | null
  | { type: 'workflowCreated' }
  | {
      type: 'subAccounts';
      perRail: Record<string, RailStats>;
      failedPermission: number;
      totalSubAccounts: number;
      failedSubAccountNames: string[];
    };

const CreateWorkflowContainer: FunctionComponent<CreateWorkflowContainerProps> = ({
  bearer,
  accountId,
  onBack,
  onCreated,
  onNavigateToRoles,
}) => {
  const history = useHistory();
  const [isReview, setIsReview] = useState(false);
  const [pendingDialog, setPendingDialog] = useState<PendingDialog>(null);
  const { workflows, fetchWorkflows } = useFetchWorkflows({ bearer });
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [workflow, setWorkflow] = useState<UpdateWorkflowContainer | undefined>(undefined);
  const [, , createWorkFlowAsync] = usePostCreateWorkflow({});
  const [, , deleteWorkflowAsync] = useDeleteWorkflow({});
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const { approvalListReset, transactionsReset } = useApprovalWorkflow();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const alertDialog = useAlertDialog();
  const toast = useToast();

  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const {
    response: { Country },
  } = useClientData();

  useEffect(() => {
    fetchWorkflows().catch(() => alertDialog.error(tGlobal('error'), tGlobal('errorOccurred')));
    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  const createWorkflow = async () => {
    if (!workflow?.values) return;
    const { TransactionTypes, tiers, applyToSubAccounts } = workflow.values;
    const filteredTiers = tiers?.filter((tier) => tier.approversNeeded !== '') || [];

    try {
      const hasExistingWorkflows = workflows.length > 0;

      for (const transactionType of TransactionTypes) {
        for (const tier of filteredTiers) {
          const res = await createWorkFlowAsync(
            {
              TransactionType: transactionType,
              NumberApprovalsRequired: sanitizeToNumeric(tier.approversNeeded) || '0',
              MinAmount: sanitizeToNumeric(tier.startAmount || '0') || '0',
              MaxAmount: tier.noUpperLimit ? null : sanitizeToNumeric(tier.endAmount),
              ApplyToAllSubaccounts: applyToSubAccounts,
            },
            bearer,
          );
          if (!res.Success) throw Error(res.ErrorMessage);
        }
      }

      if (applyToSubAccounts) {
        const { perRail, failedPermission, totalSubAccounts, failedSubAccountNames } = await propagateToSubAccounts(
          TransactionTypes,
          filteredTiers,
          {
            setDataFetchingInfo,
            fetchSubAccountsAsync,
            fetchWorkflows,
            createWorkFlowAsync,
            deleteWorkflowAsync,
            accountId,
          },
        );
        const adjustedPerRail = Object.fromEntries(
          Object.entries(perRail).map(([rail, stats]) => {
            const total = stats.created + stats.replaced;
            return hasExistingWorkflows
              ? [rail, { created: 0, replaced: total }]
              : [rail, { created: total, replaced: 0 }];
          }),
        );

        setPendingDialog({
          type: 'subAccounts',
          perRail: adjustedPerRail,
          failedPermission,
          totalSubAccounts,
          failedSubAccountNames,
        });
      } else {
        toast.success(
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.Update.successfullyCreatedToast"
            ns="translations"
            defaultValue="<0>Workflow Create</0> The workflow has been created successfully."
            components={[<span className={styles.bold} />]}
          />,
        );
        setPendingDialog({ type: 'workflowCreated' });
      }
    } catch (err) {
      fetchWorkflows();
      alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
    }
  };

  const handleDialogResult = (navigateToRoles: boolean) => {
    setPendingDialog(null);
    transactionsReset();
    approvalListReset();
    if (navigateToRoles) {
      onNavigateToRoles ? onNavigateToRoles() : history.push('/transaction-approval/configuration/roles');
    } else {
      onCreated ? onCreated() : history.push('/transaction-approval/configuration/workflows');
    }
  };

  return (
    <div className={styles.page}>
      {!onBack && (
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/transaction-approval/configuration/workflows');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
          </UtilButton>

          <Title level="1">
            {isReview
              ? t('reviewTitle', { defaultValue: 'Review Workflow' })
              : t('createTitle', { defaultValue: 'Create Workflow' })}
          </Title>
        </Box>
      )}
      <div className={styles.formWrapper}>
        <CreateWorkflowForm
          defaultValues={workflow?.values}
          isReview={isReview}
          onChange={(values, isValid) => setWorkflow({ values, isValid })}
          workflows={workflows}
          transactionTypesResponse={transactionTypesResponse}
          isSubAccount={!!bearer}
        />
      </div>
      <Box jc="fe" height="auto">
        <Box gapSize="12px">
          {isReview && (
            <Button
              id="approval-edit-review"
              colorScheme="secondary"
              iconLeftProps={{ icon: 'edit', fillAmount: 1 }}
              onClick={() => setIsReview(false)}
            >
              {t('Buttons.edit', { defaultValue: 'Edit' })}
            </Button>
          )}
          <Button
            id="approval-create-workflow"
            disabled={!workflow?.isValid}
            colorScheme="primary"
            onClick={() => {
              if (!isReview) {
                setIsReview(true);
              } else {
                createWorkflow();
              }
            }}
          >
            {isReview
              ? t('Buttons.createWorkflow', { defaultValue: 'Create Workflow' })
              : t('Buttons.review', { defaultValue: 'Review' })}
          </Button>
        </Box>
      </Box>
      {/* Modal used directly — SideDrawer's transform creates a stacking context that breaks the dialog provider's z-index */}
      {pendingDialog !== null && (
        <Modal isVisible={true} enableBgClick={false}>
          {pendingDialog.type === 'workflowCreated' ? (
            <WorkflowCreatedDialog
              onConfirm={() => handleDialogResult(true)}
              onCancel={() => handleDialogResult(false)}
            />
          ) : (
            <SubAccountsWorkflowDialog
              perRail={pendingDialog.perRail}
              failedPermission={pendingDialog.failedPermission}
              totalSubAccounts={pendingDialog.totalSubAccounts}
              failedSubAccountNames={pendingDialog.failedSubAccountNames}
              onConfirm={() => handleDialogResult(true)}
              onCancel={() => handleDialogResult(false)}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default CreateWorkflowContainer;
